import {
	Menu,
	MenuButton,
	MenuItem,
	MenuItems,
	Transition,
} from '@headlessui/react'
import { Fragment, useState } from 'react'
import {
	CountersignIcon,
	DeleteIcon,
	EyeIcon,
	MetadataIcon,
	OpenInNewIcon,
	RevokeIcon,
	SignIcon,
	XIcon,
	StarIcon,
	StarRemoveIcon
} from '../icons'
import { TypographyLabel, TypographyParagraph } from '../typography'
import { Modal } from '../modalsAndOverlays'
import { JsonViewer } from './jsonViewer'
import InternalLink from '../common/internalLink'
import { useDynamicContext } from '@dynamic-labs/sdk-react-core'
import { User } from '#types/loreTypes.tsx'

export default function PopupMenu({
	menuButton,
	metadataEntry,
	metadataType,
	walletAddress,
	onPendingSign,
	onDelete,
	onCountersign,
	onRevoke,
	onHighlight,
	onRemoveHighlight,
	nftProfileUrl,
	metadataProfileUrl,
	collectionProfileUrl,
	profileUser,
	highlightedAttestations = [],
}: {
	menuButton: any
	metadataEntry: any
	metadataType: string
	walletAddress?: string
	onPendingSign?: (metadataEntry: any) => void
	onDelete?: (metadataEntry: any) => void
	onCountersign?: (metadataEntry: any) => void
	onRevoke?: (metadataEntry: any) => void
	onHighlight?: (metadataEntry: any) => void
	onRemoveHighlight?: (metadataEntry: any) => void
	nftProfileUrl?: string
	metadataProfileUrl?: string
	collectionProfileUrl?: string
	profileUser?: User
	highlightedAttestations?: any[]
}) {
	const [JSONModalOpen, setJSONModalOpen] = useState(false)
	const { user, primaryWallet } = useDynamicContext()

	const metadataObjectToJSON = () => {
		return Object.keys(metadataEntry)
			.filter(
				key =>
					key != 'nftName' &&
					key != 'nftCollection' &&
					key != 'nft' &&
					key != 'userId' &&
					key != 'userAvatar' &&
					key != 'username' &&
					key != 'nftContentType' &&
					key != 'nftThumbnail' &&
					key != 'isRevoked' &&
					key != 'isReattested' &&
					key != 'countersignStatus' &&
					key != 'nftImage',
			)
			.reduce((obj, key) => {
				//@ts-ignore
				obj[key] = metadataEntry[key]
				return obj
			}, {})
	}

	const canSignDraft = () => {
		return (
			!metadataEntry.signature &&
			metadataEntry.address &&
			walletAddress &&
			metadataEntry.address.toLowerCase() == walletAddress.toLowerCase()
		)
	}

	const canDeleteDraft = () => {
		return (
			!metadataEntry.signature &&
			metadataEntry.address &&
			walletAddress &&
			metadataEntry.address.toLowerCase() == walletAddress.toLowerCase()
		)
	}

	const canHighlight = () => {
		return (
			primaryWallet &&
			metadataEntry.address &&
			primaryWallet.address.toLowerCase() === metadataEntry.address.toLowerCase() &&
			metadataEntry.template.slug !== 'offchainSale' && // These types are not supported for highlighting
			!highlightedAttestations.some( // Check if already highlighted
				attestation => attestation.references.attestationIPFSCID === metadataEntry.references.attestationIPFSCID
			) &&
			highlightedAttestations.length < (parseInt(import.meta.env.VITE_MAX_HIGHLIGHTED_ATTESTATIONS ? import.meta.env.VITE_MAX_HIGHLIGHTED_ATTESTATIONS : 4))
		)
	}

	const canRemoveHighlight = () => {
		return profileUser?.wallets.some(wallet => wallet.address.toLowerCase() === primaryWallet?.address.toLowerCase())
	}

	const canCountersign = () => {
		return (
			metadataEntry.properties &&
			walletAddress &&
			!metadataEntry.isReattested &&
			(((metadataEntry.properties.artistInitiated ||
				metadataEntry.properties.artistInitiated === undefined) &&
				metadataEntry.properties.receiverAddress &&
				metadataEntry.properties.receiverAddress.toLowerCase() ==
					walletAddress.toLowerCase()) ||
				(metadataEntry.properties.artistInitiated === false &&
					metadataEntry.properties.ownerAddress &&
					metadataEntry.properties.ownerAddress.toLowerCase() ==
						walletAddress.toLowerCase()))
		)
	}

	const canRevoke = () => {
		if (
			user &&
			user.metadata &&
			user.metadata.accessLevel &&
			user.metadata.accessLevel.createCollection
		) {
			return (
				metadataEntry.signature &&
				!metadataEntry.isRevoked &&
				walletAddress &&
				metadataEntry.address.toLowerCase() == walletAddress.toLowerCase()
			)
		} else {
			return (
				metadataEntry.signature &&
				!metadataEntry.isRevoked &&
				walletAddress &&
				metadataEntry.address.toLowerCase() == walletAddress.toLowerCase() &&
				metadataEntry.template.slug !== 'collection'
			)
		}
	}

	return (
		<>
			<Menu as="div" className="relative inline-block text-left">
				<div className="text-textPrimaryLight dark:text-textPrimaryDark">
					<MenuButton>{menuButton}</MenuButton>
				</div>
				<Transition
					as={Fragment}
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
				>
					<MenuItems
						className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md border border-uiPrimaryDark [--anchor-gap:8px] [--anchor-padding:8px] focus:outline-none dark:border-uiPrimaryLight overflow-hidden"
						anchor="bottom start"
					>
						<div className="rounded-md bg-textPrimaryLight dark:bg-white">							
							{canHighlight() && onHighlight && (
								<MenuItem>
									<button
										className="group flex w-full items-center rounded-md px-3 py-3 text-textSecondaryDark hover:bg-uiSecondaryDark dark:hover:bg-uiSecondaryLight hover:text-textPrimaryDark dark:text-textSecondaryLight dark:hover:text-textPrimaryLight"
										onClick={() => onHighlight(metadataEntry)}
									>
										<div className="text-textSecondaryDark group-hover:text-textPrimaryDark dark:text-textSecondaryLight dark:group-hover:text-textPrimaryLight">
											<StarIcon />
										</div>
										<TypographyLabel
											size="medium"
											isBold={true}
											additionalClasses="ml-3"
										>
											Highlight
										</TypographyLabel>
									</button>
								</MenuItem>
							)}
							{canRemoveHighlight() && onRemoveHighlight && (
								<MenuItem>
									<button
										className="group flex w-full items-center rounded-md px-3 py-3 text-textSecondaryDark hover:bg-uiSecondaryDark dark:hover:bg-uiSecondaryLight hover:text-textPrimaryDark dark:text-textSecondaryLight dark:hover:text-textPrimaryLight"
										onClick={() => onRemoveHighlight(metadataEntry)}
									>
										<div className="text-textSecondaryDark group-hover:text-textPrimaryDark dark:text-textSecondaryLight dark:group-hover:text-textPrimaryLight">
											<StarRemoveIcon />
										</div>
										<TypographyLabel
											size="medium"
											isBold={true}
											additionalClasses="ml-3"
										>
											Remove Highlight
										</TypographyLabel>
									</button>
								</MenuItem>
							)}
							{metadataProfileUrl &&
								metadataProfileUrl !== '' &&
								metadataProfileUrl.indexOf('undefined') < 0 && (
									<MenuItem>
										<InternalLink to={metadataProfileUrl} prefetch="none">
											<button className="group flex w-full items-center rounded-md px-3 py-3 text-textSecondaryDark hover:bg-uiSecondaryDark dark:hover:bg-uiSecondaryLight hover:text-textPrimaryDark dark:text-textSecondaryLight dark:hover:text-textPrimaryLight">
												<div className="text-textSecondaryDark group-hover:text-textPrimaryDark dark:text-textSecondaryLight dark:group-hover:text-textPrimaryLight">
													<EyeIcon aria-hidden="true" />
												</div>
												<TypographyLabel
													size="medium"
													isBold={true}
													additionalClasses="ml-3"
												>
													View Provenance
												</TypographyLabel>
											</button>
										</InternalLink>
									</MenuItem>
								)}
							{collectionProfileUrl &&
								collectionProfileUrl !== '' &&
								collectionProfileUrl.indexOf('undefined') < 0 && (
									<MenuItem>
										<InternalLink to={collectionProfileUrl} prefetch="none">
											<button className="group flex w-full items-center rounded-md px-3 py-3 text-textSecondaryDark hover:bg-uiSecondaryDark dark:hover:bg-uiSecondaryLight hover:text-textPrimaryDark dark:text-textSecondaryLight dark:hover:text-textPrimaryLight">
												<div className="text-textSecondaryDark group-hover:text-textPrimaryDark dark:text-textSecondaryLight dark:group-hover:text-textPrimaryLight">
													<EyeIcon aria-hidden="true" />
												</div>
												<TypographyLabel
													size="medium"
													isBold={true}
													additionalClasses="ml-3"
												>
													View Collection
												</TypographyLabel>
											</button>
										</InternalLink>
									</MenuItem>
								)}
							{nftProfileUrl &&
								nftProfileUrl !== '' &&
								nftProfileUrl.indexOf('undefined') < 0 && (
									<MenuItem>
										<InternalLink
											to={nftProfileUrl}
											target="_blank"
											prefetch="none"
										>
											<button className="group flex w-full items-center rounded-md px-3 py-3 text-textSecondaryDark hover:bg-uiSecondaryDark dark:hover:bg-uiSecondaryLight hover:text-textPrimaryDark dark:text-textSecondaryLight dark:hover:text-textPrimaryLight">
												<div className="text-textSecondaryDark group-hover:text-textPrimaryDark dark:text-textSecondaryLight dark:group-hover:text-textPrimaryLight">
													<OpenInNewIcon aria-hidden="true" />
												</div>
												<TypographyLabel
													size="medium"
													isBold={true}
													additionalClasses="ml-3"
												>
													Open NFT Profile
												</TypographyLabel>
											</button>
										</InternalLink>
									</MenuItem>
								)}
							{canSignDraft() && onPendingSign && (
								<MenuItem>
									<button
										className="group flex w-full items-center rounded-md px-3 py-3 text-textSecondaryDark hover:bg-uiSecondaryDark dark:hover:bg-uiSecondaryLight hover:text-textPrimaryDark dark:text-textSecondaryLight dark:hover:text-textPrimaryLight"
										onClick={() => onPendingSign(metadataEntry)}
									>
										<div className="text-textSecondaryDark group-hover:text-textPrimaryDark dark:text-textSecondaryLight dark:group-hover:text-textPrimaryLight">
											<SignIcon className="mr-3 h-5 w-5" aria-hidden="true" />
										</div>
										<TypographyLabel
											size="medium"
											isBold={true}
											additionalClasses="ml-3"
										>
											{metadataEntry.template.slug === 'collection'
												? 'Edit'
												: 'Sign'}
										</TypographyLabel>
									</button>
								</MenuItem>
							)}
							{canSignDraft() &&
								onPendingSign &&
								metadataEntry.template.slug === 'collection' && (
									<MenuItem>
										<button
											className="group flex w-full items-center rounded-md px-3 py-3 text-textSecondaryDark hover:bg-uiSecondaryDark dark:hover:bg-uiSecondaryLight hover:text-textPrimaryDark dark:text-textSecondaryLight dark:hover:text-textPrimaryLight"
											onClick={() =>
												window.open(
													`${window.location.origin}/collectionPreview/${metadataEntry.attestationUid}`,
												)
											}
										>
											<div className="text-textSecondaryDark group-hover:text-textPrimaryDark dark:text-textSecondaryLight dark:group-hover:text-textPrimaryLight">
												<EyeIcon className="mr-3 h-5 w-5" aria-hidden="true" />
											</div>
											<TypographyLabel
												size="medium"
												isBold={true}
												additionalClasses="ml-3"
											>
												Preview Collection
											</TypographyLabel>
										</button>
									</MenuItem>
								)}
							<MenuItem>
								<button
									className="group flex w-full items-center rounded-md px-3 py-3 text-textSecondaryDark hover:bg-uiSecondaryDark dark:hover:bg-uiSecondaryLight hover:text-textPrimaryDark dark:text-textSecondaryLight dark:hover:text-textPrimaryLight"
									onClick={() => setJSONModalOpen(true)}
								>
									<div className="text-textSecondaryDark group-hover:text-textPrimaryDark dark:text-textSecondaryLight dark:group-hover:text-textPrimaryLight">
										<MetadataIcon
											className="ml-0.5 mr-2 h-5 w-5"
											aria-hidden="true"
										/>
									</div>
									<TypographyLabel
										size="medium"
										isBold={true}
										additionalClasses="ml-2"
									>
										View Metadata
									</TypographyLabel>
								</button>
							</MenuItem>
							{metadataProfileUrl &&
								metadataProfileUrl !== '' &&
								metadataProfileUrl.indexOf('undefined') < 0 && (
									<MenuItem>
										<a
											href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out this ${metadataType} on Atomic Form:`)}&url=${encodeURIComponent(metadataProfileUrl)}`}
											target="_blank"
										>
											<button className="group flex w-full items-center rounded-md px-3 py-3 text-textSecondaryDark hover:bg-uiSecondaryDark dark:hover:bg-uiSecondaryLight hover:text-textPrimaryDark dark:text-textSecondaryLight dark:hover:text-textPrimaryLight">
												<div className="text-textSecondaryDark group-hover:text-textPrimaryDark dark:text-textSecondaryLight dark:group-hover:text-textPrimaryLight">
													<XIcon className="mr-2 h-5 w-5" aria-hidden="true" />
												</div>
												<TypographyLabel
													size="medium"
													isBold={true}
													additionalClasses="ml-2"
												>
													Share
												</TypographyLabel>
											</button>
										</a>
									</MenuItem>
								)}
							{canDeleteDraft() && onDelete && (
								<MenuItem>
									<button
										className="group flex w-full items-center rounded-md px-3 py-3 text-textSecondaryDark hover:bg-uiSecondaryDark dark:hover:bg-uiSecondaryLight hover:text-textPrimaryDark dark:text-textSecondaryLight dark:hover:text-textPrimaryLight"
										onClick={() => onDelete(metadataEntry)}
									>
										<div className="text-textSecondaryDark group-hover:text-textPrimaryDark dark:text-textSecondaryLight dark:group-hover:text-textPrimaryLight">
											<DeleteIcon className="mr-3 h-5 w-5" aria-hidden="true" />
										</div>
										<TypographyLabel
											size="medium"
											isBold={true}
											additionalClasses="ml-3"
										>
											Delete
										</TypographyLabel>
									</button>
								</MenuItem>
							)}
							{canCountersign() && onCountersign && (
								<MenuItem>
									<button
										className="group flex w-full items-center rounded-md px-3 py-3 text-textSecondaryDark hover:bg-uiSecondaryDark dark:hover:bg-uiSecondaryLight hover:text-textPrimaryDark dark:text-textSecondaryLight dark:hover:text-textPrimaryLight"
										onClick={() => onCountersign(metadataEntry)}
									>
										<div className="text-textSecondaryDark group-hover:text-textPrimaryDark dark:text-textSecondaryLight dark:group-hover:text-textPrimaryLight">
											<CountersignIcon
												className="mr-3 h-5 w-5"
												aria-hidden="true"
											/>
										</div>
										<TypographyLabel
											size="medium"
											isBold={true}
											additionalClasses="ml-3"
										>
											Countersign
										</TypographyLabel>
									</button>
								</MenuItem>
							)}
							{canRevoke() && onRevoke && (
								<MenuItem>
									<button
										className="group flex w-full items-center rounded-md px-3 py-3 text-textSecondaryDark hover:bg-uiSecondaryDark dark:hover:bg-uiSecondaryLight hover:text-textPrimaryDark dark:text-textSecondaryLight dark:hover:text-textPrimaryLight"
										onClick={() => onRevoke(metadataEntry)}
									>
										<div className="text-textSecondaryDark group-hover:text-textPrimaryDark dark:text-textSecondaryLight dark:group-hover:text-textPrimaryLight">
											<RevokeIcon className="mr-3 h-5 w-5" aria-hidden="true" />
										</div>
										<TypographyLabel
											size="medium"
											isBold={true}
											additionalClasses="ml-3"
										>
											Revoke
										</TypographyLabel>
									</button>
								</MenuItem>
							)}
						</div>
					</MenuItems>
				</Transition>
			</Menu>
			<Modal
				size="large"
				title="Raw NFT metadata"
				body={
					<Fragment>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight"
						>
							Explore the metadata to see how you've immortalized this NFT by
							signing offchain history.
						</TypographyParagraph>
						<div className="mt-4 hidden">
							<JsonViewer src={metadataObjectToJSON()} theme={'light'} />
						</div>
						<div className="mt-4 dark:block">
							<JsonViewer src={metadataObjectToJSON()} theme={'dark'} />
						</div>
					</Fragment>
				}
				isOpen={JSONModalOpen}
				onClose={() => setJSONModalOpen(false)}
			/>
		</>
	)
}
